'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useDropzone } from 'react-dropzone';
import ReactPlayer from 'react-player';

interface VideoPlayerProps {
  videoUrl: string;
  onVideoChange: (newUrl: string) => void;
  currentTime?: number;
  onProgress?: (progress: number) => void;
}

export default function VideoPlayer({
  videoUrl,
  onVideoChange,
  currentTime,
  onProgress,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newVideoUrl = URL.createObjectURL(file);
      onVideoChange(newVideoUrl);
    },
    [onVideoChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': [] },
    multiple: false,
  });

  useEffect(() => {
    if (videoRef.current && currentTime !== undefined) {
      videoRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  return (
    <div {...getRootProps()} className="relative aspect-video w-full">
      {videoUrl?.includes('youtube') ||
      videoUrl?.includes('youtu.be') ||
      videoUrl?.includes('vimeo') ? (
        <ReactPlayer
          ref={videoRef}
          url={videoUrl}
          onProgress={onProgress}
          width="100%"
          height="100%"
          controls
        />
      ) : (
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          src={videoUrl}
          controls
        />
      )}
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <p className="text-center text-white">
            {isDragActive
              ? 'Drop the video here'
              : 'Drag and drop a video file here, or click to select'}
          </p>
        </div>
      )}
    </div>
  );
}
